import {
  CellFocusedEvent,
  Column,
  GROUP_AUTO_COLUMN_ID,
} from "@ag-grid-community/core";
import { GenericTableRow } from "gen/model/genericTableRow";
import { DataTableContext } from "shared/components/table/data/DataTableContext";
import {
  COLUMN_ID_ACTIONS,
  COLUMN_ID_AUTO_GROUP,
} from "shared/components/table/data/internal/DataTableConstants";
import { buildDataTableColumnContext } from "shared/components/table/data/internal/DataTableContextBuilders";
import { isCellSkipped } from "shared/components/table/data/internal/DataTableInternalUtils";

let lastKey: string | null = null;
window.addEventListener("keydown", (event) => {
  lastKey = event.key;
});
window.addEventListener("keyup", () => {
  lastKey = null;
});

/**
 * Handles cell focus in a data table.
 *
 * If a focused cell is obscured by a cell above with a row span
 * it adjusts focus to the nearest visible cell above or below.
 */
export const onCellFocused = (event: CellFocusedEvent<GenericTableRow>) => {
  const rowIndex = event.rowIndex;
  if (rowIndex === null) {
    return;
  }
  const row = event.api.getDisplayedRowAtIndex(rowIndex);
  if (!row) {
    return;
  }
  const column = event.column as Column;
  if (
    column.getColId() === COLUMN_ID_AUTO_GROUP ||
    column.getColId() === COLUMN_ID_ACTIONS ||
    column.getColId() === GROUP_AUTO_COLUMN_ID
  ) {
    return;
  }
  const cell = event.api.getCellValue({
    colKey: column.getColId(),
    rowNode: row,
  });
  const columnContext = buildDataTableColumnContext(event.context, column);
  const tableContext = event.context as DataTableContext;
  const isFocusedCellSkipped = isCellSkipped({
    api: event.api,
    tableContext,
    columnContext,
    row,
    cell,
  });
  if (isFocusedCellSkipped) {
    const searchForCell = (start: number, end: number, step: number) => {
      for (let i = start; step > 0 ? i <= end : i >= end; i += step) {
        const iRow = event.api.getDisplayedRowAtIndex(i);
        if (!iRow) {
          return null;
        }
        const iCell = event.api.getCellValue({
          colKey: column.getColId(),
          rowNode: iRow,
        });
        const isThisCellSkipped = isCellSkipped({
          api: event.api,
          tableContext,
          columnContext,
          row,
          cell: iCell,
        });
        if (!isThisCellSkipped) {
          return i;
        }
      }
      return end;
    };
    if (lastKey === "ArrowDown") {
      const nextRowIndex = searchForCell(
        rowIndex + 1,
        event.api.getDisplayedRowCount(),
        1,
      );
      if (nextRowIndex !== null) {
        event.api.setFocusedCell(nextRowIndex, column);
      } else if (event.api.getPinnedBottomRowCount() > 0) {
        event.api.setFocusedCell(0, column, "bottom");
      } else {
        event.api.setFocusedCell(rowIndex, column);
      }
    } else {
      const prevRowIndex = searchForCell(rowIndex - 1, 0, -1);
      if (prevRowIndex !== null) {
        event.api.setFocusedCell(prevRowIndex, column);
      }
    }
  }
};
